import { Button } from '@rcg/ui-components';
import styled, { AnyStyledComponent } from 'styled-components';
import colors from 'styles/colors';
import sizes from 'styles/sizes';
import { BrandCode } from 'types';
import { CruiseCardBase } from '../CruiseCard.styles';

type CardnNameProps = {
	isLargerName: boolean;
};

export const RefinedCruiseCardBase = styled(CruiseCardBase)`
	display: flex;
	flex-direction: column;
	${({ theme }) =>
		({
			[BrandCode.Celebrity]: `
      min-height: 280px;
    `,
			[BrandCode.RoyalCaribbean]: `
      min-height: 270px;
    `,
		})[theme.brandCode]}

	@media (min-width: ${sizes.media.tabletLandscape}px) {
		flex-direction: row;
	}

	@media (min-width: ${sizes.media.mediumMaxWidth}px) {
		max-width: 1300px;
	}
`;

export const RefinedCruiseCardImage = styled.div`
	z-index: 0;
	padding: 20px;
	position: relative;
	${({ theme }) =>
		({
			[BrandCode.Celebrity]: `
      min-height: 230px;
      
      @media (min-width: ${sizes.media.tabletPortrait}px) {
        min-height: 251px;
      }

      @media (min-width: ${sizes.media.tabletLandscape}px) {
        min-height: auto;
        min-width: 55%;
      }
    `,
			[BrandCode.RoyalCaribbean]: `
      min-height: 250px;
      
      @media (min-width: ${sizes.media.tabletLandscape}px) {
        min-height: auto;
        min-width: 55%;
      }
    `,
		})[theme.brandCode]}

	&::before {
		content: '';
		position: absolute;
		top: 0;
		left: 0;
		right: 0;
		bottom: 0;
		background: linear-gradient(180deg, rgba(0, 0, 0, 0) 32.22%, rgba(0, 0, 0, 0.7) 100%);
		z-index: 1;
	}

	& img {
		object-fit: cover;
		object-position: center;
	}
`;

export const RefinedCruiseCardTitle = styled.div`
	align-items: flex-start;
	bottom: 0;
	color: ${({ theme }) => theme.palette.text.primary};
	display: flex;
	flex-direction: column;
	justify-content: center;
	left: 0;
	position: absolute;
	width: 100%;
	z-index: 2;
	${({ theme }) =>
		({
			[BrandCode.Celebrity]: `
      gap: 5px;
      padding: 20px 25px;

      @media (min-width: ${sizes.media.tabletLandscape}px) {
        gap: 10px;
        padding: 20px;
      }
    `,
			[BrandCode.RoyalCaribbean]: `
      gap: 10px;
      padding: 20px;

      @media (min-width: ${sizes.media.tabletPortrait}px) {
        padding: 30px 20px;
      }

      @media (min-width: ${sizes.media.tabletLandscape}px) {
        padding: 20px;
      }
    `,
		})[theme.brandCode]}
`;

export const BadgeRow = styled.div`
	display: flex;
	flex-wrap: wrap;
	gap: 8px;
	align-items: center;
`;

export const RefinedCruiseCardTotalNights = styled.h3`
	align-items: center;
	color: ${({ theme }) => theme.palette.text.primary};
	font-family: ${({ theme }) => theme.typography.fontFamilySemiBold};
	display: flex;
	justify-content: center;
	padding: 5px 10px;
	${({ theme }) =>
		({
			[BrandCode.Celebrity]: `
      background-color: rgba(255, 255, 255, 0.85);
      font-size: 12px;
      height: 25px;
      letter-spacing: 0.24px;
      line-height: 15px;
      text-transform: uppercase;

      @media (min-width: ${sizes.media.tabletPortrait}px) {
        font-size: 18px;
        height: 43px;
        letter-spacing: 0.36px;
        line-height: 27px;
        padding: 10px;
      }

      @media (min-width: ${sizes.media.mediumMaxWidth}px) {
        font-size: 15px;
        height: 39px;
        letter-spacing: 0.32px;
        line-height: 24px;
      }
    `,
			[BrandCode.RoyalCaribbean]: ` 
      background-color: ${theme.palette.background.paper};
      font-family: ${theme.typography.fontFamilyBold};  
      font-size: 14px;
      height: 28px;
      letter-spacing: -0.28px;
      line-height: 17px;

      @media (min-width: ${sizes.media.mediumMaxWidth}px) {
        font-size: 16px;
        height: 30px;
        letter-spacing: -0.32px;
        line-height: 19px;
      }
    `,
		})[theme.brandCode]}
`;

/* Color #299BE1 is not part of the @rcg/ui-components library theme */
export const RefinedCruiseCardRiverBadge = styled.h3`
	align-items: center;
	color: ${({ theme }) => theme.palette.common.white};
	font-family: ${({ theme }) => theme.typography.fontFamilySemiBold};
	display: flex;
	justify-content: center;
	padding: 5px 10px;
	${({ theme }) =>
		({
			[BrandCode.Celebrity]: `
      background-color: ${colors.celebrity.riverBadge}; 
      font-size: 12px;
      height: 25px;
      letter-spacing: 0.24px;
      line-height: 15px;
      text-transform: uppercase;

      @media (min-width: ${sizes.media.tabletPortrait}px) {
        font-size: 18px;
        height: 43px;
        letter-spacing: 0.36px;
        line-height: 27px;
        padding: 10px;
      }

      @media (min-width: ${sizes.media.mediumMaxWidth}px) {
        font-size: 15px;
        height: 39px;
        letter-spacing: 0.32px;
        line-height: 24px;
      }
    `,
		})[theme.brandCode]}
`;

export const RefinedCruiseCardName = styled.h4<CardnNameProps>`
	color: ${({ theme }) => theme.palette.common.white};
	font-family: ${({ theme }) => theme.typography.fontFamilySemiBold};
	${({ theme, isLargerName }) =>
		({
			[BrandCode.Celebrity]: `
      font-family: ${theme.typography.fontFamily};
      font-size: ${isLargerName ? '20px' : '32px'};
      letter-spacing: 0.64px;
      line-height: 40px;
    
      @media (min-width: ${sizes.media.tabletPortrait}px) {
        font-size: 42px;
        letter-spacing: 0.80px;
        line-height: 52px;
      }
      @media (min-width: ${sizes.media.smallMaxWidth}px) {
        font-size: 36px;
        letter-spacing: 0.72px;
        line-height: 45px;
      }
      `,
			[BrandCode.RoyalCaribbean]: `  
      font-family: ${theme.typography.fontFamilySemiBold}; 
      font-size: 24px;
      letter-spacing: -0.48px;
      line-height: 28px;

      @media (min-width: ${sizes.media.tabletPortrait}px) {
        font-size: 32px;
        letter-spacing: -0.64px;
        line-height: 37px;
      }

      @media (min-width: ${sizes.media.mediumMaxWidth}px) {
        font-size: 42px;
        letter-spacing: -0.84px;
        line-height: 48px;
      }
    `,
		})[theme.brandCode]}
`;

export const RefinedCruiseCardDetails = styled.div`
	align-items: flex-start;
	align-self: stretch;
	display: flex;
	flex-direction: column;
	justify-content: flex-start;
	width: 100%;
	${({ theme }) =>
		({
			[BrandCode.Celebrity]: `
      gap: 20px;
      padding: 20px;

      @media (min-width: ${sizes.media.tabletPortrait}px) {
        padding: 20px 30px 30px 30px;
      }

      @media (min-width: ${sizes.media.tabletLandscape}px) {
        padding: 20px;
      }
    `,
			[BrandCode.RoyalCaribbean]: `
      gap: 10px;
      padding: 10px 20px 20px 20px;
    `,
		})[theme.brandCode]}

	@media (min-width: ${sizes.media.tabletPortrait}px) {
		gap: 20px;
		padding: 20px;
	}

	@media (min-width: ${sizes.media.mediumMaxWidth}px) {
		padding: 20px 25px;
	}
`;

export const RefinedCruiseCardShipSocials = styled.div`
	align-items: center;
	display: flex;
	justify-content: space-between;
	margin-bottom: -6px;
	margin-top: -6px;
	width: 100%;

	@media (min-width: ${sizes.media.tabletPortrait}px) {
		margin-bottom: -5px;
		margin-top: -5px;
	}
`;

export const RefinedCruiseCardCompareSocials = styled(RefinedCruiseCardShipSocials)``;

export const RefinedCruiseCardSocials = styled.div`
	display: flex;
	gap: 10px;
`;

export const RefinedCruiseCardPriceDetail = styled.div`
	align-items: center;
	border-top: 1px solid ${({ theme }) => theme.palette.grey[400]};
	display: flex;
	flex-direction: column;
	padding-top: 20px;
	width: 100%;
	${({ theme }) =>
		({
			[BrandCode.Celebrity]: `
      @media (min-width: ${sizes.media.tabletPortrait}px) {
        flex-direction: row;
        justify-content: space-between;
      }

      @media (min-width: ${sizes.media.tabletLandscape}px) {
        flex-direction: column;
        justify-content: center;
        gap: 20px;
        padding-top: 30px;
      }

      @media (min-width: ${sizes.media.mediumMaxWidth}px) {
        flex-direction: row;
        justify-content: space-between;
        gap: 0;
      }
    `,
			[BrandCode.RoyalCaribbean]: `
      gap: 10px;

      @media (min-width: ${sizes.media.tabletPortrait}px) {
        align-items: flex-end;
        flex-direction: row;
        justify-content: space-between;
      }
    `,
		})[theme.brandCode]}
`;

export const RefinedCruiseCardButtonsContainer = styled.div`
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: flex-start;
	gap: 10px;
`;

export const RefinedCruiseCardButton = styled(Button as AnyStyledComponent)`
	${({ theme }) =>
		({
			[BrandCode.Celebrity]: `
      width: 184px !important;
      padding: 5px 16px !important;
  `,
			[BrandCode.RoyalCaribbean]: `
      align-self: stretch !important;
      height: unset !important;
      padding: 13px 50px !important;
      
      @media (max-width: ${sizes.media.tabletLandscape}px) {
        font-size: 14px !important;
      }
  `,
		})[theme.brandCode]}
`;

export const RefinedCruiseCardPriceButton = styled(RefinedCruiseCardButton)`
	${({ theme }) =>
		({
			[BrandCode.Celebrity]: `
      background-color: ${theme.palette.accent.main} !important;
      &.rebranding  {
        background-color: ${colors.celebrity.lightOrange} !important;
      } 
      &.rebranding:hover {
        background-color: ${colors.celebrity.darkOrange} !important;
      }  
    `,
		})[theme.brandCode]}
`;

export const RefinedCruiseCardItineraryButton = styled(RefinedCruiseCardButton)`
	${({ theme }) =>
		({
			[BrandCode.Celebrity]: `
      border-color: ${theme.palette.secondary.main} !important;
      color: ${theme.palette.secondary.main} !important;
    `,
		})[theme.brandCode]}
`;
